<script setup lang="js">
import { Bootstrap5Pagination } from 'laravel-vue-pagination';
import { onMounted } from 'vue';
import { useBusinessProcessStore } from '@/stores/businessProcess.store';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import Search from '@/components/Search.vue';
import Sort from '@/components/Sort.vue';
import BusinessProcessCreateModal from '@/components/modals/BusinessProcessCreateModal.vue';
import BusinessProcessCopyModal from '@/components/modals/BusinessProcessCopyModal.vue';
import BusinessProcessUpdateModal from '@/components/modals/BusinessProcessUpdateModal.vue';
import BusinessProcessFixateModal from '@/components/modals/BusinessProcessFixateModal.vue';
import BusinessProcessValidateModal from '@/components/modals/BusinessProcessValidateModal.vue';
import BusinessProcessPromoteModal from '@/components/modals/BusinessProcessPromoteModal.vue';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import useModal from '@/stores/modal.store';

const router = useRouter();
const businessProcessStore = useBusinessProcessStore();

const {
  paginatedBPList,
  searchName,
  selectedBPs,
  sortType,
  isSelectAll,
  bp,
  projectId,
  deleteBP,
  getBPListRequest
} = storeToRefs(businessProcessStore);
const { activeModal, openModal } = useModal();

const types = { legal_entity: 'Юр.лицо', individual: 'Физ.лицо' };

function getList() {
  businessProcessStore.getPaginatedBPList();
}

onMounted(() => {
  getList();
});

function selectItem(itemId) {
  if (selectedBPs.value.includes(itemId)) {
    selectedBPs.value = selectedBPs.value.filter((bp) => bp != itemId);
  } else {
    selectedBPs.value.push(itemId);
  }
}
function selectAll() {
  const isActive = isSelectAll.value;
  if (!isActive) {
    const list = paginatedBPList.value.data;
    list.forEach((bp) => {
      if (!selectedBPs.value.includes(bp.id)) {
        selectedBPs.value.push(bp.id);
      }
    });
  } else {
    selectedBPs.value = [];
  }
  isSelectAll.value = !isActive;
}
function changeSortType(newValue) {
  sortType.value = newValue;
  getList();
}
function deleteAll() {
  selectedBPs.forEach((bpId) => {
    deleteBP(bpId);
  });
}
function openUpdateModal(item) {
  bp.value = item;
  openModal('businessProcessUpdateModal');
}
function openFixateModal(item) {
  bp.value = item;
  openModal('businessProcessFixateModal');
}
function openValidateModal(item) {
  bp.value = item;
  openModal('businessProcessValidateModal');
}
function openPromoteModal(item) {
  bp.value = item;
  openModal('businessProcessPromoteModal');
}
function openCopyModal(item) {
  bp.value = item;
  openModal('businessProcessCopyModal');
}
function goToRolesView(bp) {
  router.push({
    name: 'bp-roles',
    params: {
      projectId: projectId.value,
      bpId: bp.id
    }
  });
}

function handlePageChange(page) {
  businessProcessStore.getPaginatedBPList(page);
}
function redirectTo(projectId, bpId) {
  router.push({
    name: 'bp-id',
    params: {
      projectId,
      bpId
    }
  });
}
</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="d-flex justify-content-between mb-4 flex-wrap projects_filter-block">
        <div class="d-flex align-items-center flex-wrap">
          <Search
            @search="getList()"
            :value="searchName"
            :placeholderText="'Поиск по названию'"
            @input="searchName = $event.target.value"
          />
          <Sort :value="sortType" @applyChange="changeSortType" />
          <!-- <span class="delete-selected-btn" @click="deleteAll()" v-show="selectedBusinesses.length">Удалить выбранные</span> -->
        </div>
        <div class="d-flex justify-content-end flex-wrap align-items-end">
          <Bootstrap5Pagination
            :data="paginatedBPList"
            @pagination-change-page="handlePageChange"
            :limit="2"
          />
          <div @click="openModal('businessProcessCreateModal')" class="orange-btn">
            <img src="@/assets/img/plus-ico.svg" alt="" /> Создать бизнес-процесс
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="gray-table">
          <thead>
            <tr>
              <th class="checkbox-td">
                <div>
                  <span
                    @click="selectAll()"
                    class="table-checkbox"
                    :class="{ active: isSelectAll }"
                  />
                </div>
              </th>
              <th class="w-25">Название</th>
              <th class="w-50">Описание</th>
              <th class="text-nowrap">Тип</th>
              <th class="text-nowrap text-center" style="width: 1%">Версия</th>
              <th class="text-nowrap text-center" style="width: 1%">На бою</th>
              <th class="text-nowrap" style="width: 1%">Действие</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="getBPListRequest.isLoading">
              <td colspan="7">
                <div class="d-flex justify-content-center">
                  <img class="loader" src="@/assets/img/loader.gif" alt="loader.gif" />
                </div>
              </td>
            </tr>
            <tr
              v-for="businessProcess in paginatedBPList.data"
              :key="businessProcess.id"
              :class="{
                'fixation-row-style': !businessProcess.stable,
                selected: selectedBPs.includes(businessProcess.id)
              }"
              @click.exact="redirectTo(projectId, businessProcess.id)"
            >
              <td class="checkbox-td">
                <div>
                  <span @click.stop="selectItem(businessProcess.id)" class="table-checkbox" />
                </div>
              </td>
              <td class="p-0">
                <RouterLink @click.stop class="p-3 text-body d-block" :to="{ name: 'bp-id', params: { projectId, bpId: businessProcess.id } }">
                  {{ businessProcess.name }}
                </RouterLink>
              </td>
              <td class="p-0">
                <RouterLink @click.stop class="p-3 text-body d-block bp-description" :to="{ name: 'bp-id', params: { projectId, bpId: businessProcess.id } }">
                  {{ businessProcess.description }}
                </RouterLink>
              </td>
              <td class="p-0">
                <RouterLink @click.stop class="p-3 text-body d-block text-nowrap" :to="{ name: 'bp-id', params: { projectId, bpId: businessProcess.id } }">
                  {{ types[businessProcess.type] }}
                </RouterLink>
              </td>
              <td class="p-0">
                <RouterLink
                  @click.stop
                  class="p-3 text-body d-block text-nowrap text-center bp-version"
                  :title="businessProcess.stable ? '' : `черновик, зафиксировано: ${businessProcess.last_version - 1}`"
                  :to="{ name: 'bp-id', params: { projectId, bpId: businessProcess.id } }"
                >
                  {{ businessProcess.last_version }}
                  <span v-if="!businessProcess.stable" class="bp-draft-mark">черновик</span>
                </RouterLink>
              </td>
              <td class="p-0">
                <RouterLink
                  @click.stop
                  class="p-3 d-block text-nowrap text-center bp-version"
                  :class="businessProcess.prod_version ? 'text-body' : 'text-muted'"
                  :to="{ name: 'bp-id', params: { projectId, bpId: businessProcess.id } }"
                >
                  {{ businessProcess.prod_version ?? '—' }}
                </RouterLink>
              </td>
              <td>
                <div class="table-buttons">
                  <div
                    class="table-button"
                    @click.stop="openCopyModal(businessProcess)"
                    title="Копирование БП"
                  >
                    <span class="copy-ico" />
                  </div>
                  <div
                    class="table-button"
                    @click.stop="openValidateModal(businessProcess)"
                    title="валидация"
                  >
                    <span class="validate-ico" />
                  </div>
                  <div
                    class="table-button"
                    @click.stop="openFixateModal(businessProcess)"
                    title="фиксация"
                  >
                    <span class="fixate-ico" />
                  </div>
                  <div
                    class="table-button"
                    @click.stop="openPromoteModal(businessProcess)"
                    title="выложить на бой"
                  >
                    <span class="promote-ico" />
                  </div>
                  <div
                    class="table-button"
                    @click.stop="openUpdateModal(businessProcess)"
                    title="редактировать"
                  >
                    <span class="edit-ico" />
                  </div>
                  <div
                    class="table-button"
                    @click.stop="goToRolesView(businessProcess)"
                    title="роли"
                  >
                    <span class="role-ico" />
                  </div>
                  <!--
                    <div class="table-button">
                    <span class="delite-ico"/>
                    </div>
                    -->
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <BusinessProcessUpdateModal v-if="activeModal === 'businessProcessUpdateModal'" />
      <BusinessProcessCreateModal v-if="activeModal === 'businessProcessCreateModal'" />
      <BusinessProcessCopyModal v-if="activeModal === 'businessProcessCopyModal'" />
      <BusinessProcessFixateModal v-if="activeModal === 'businessProcessFixateModal'" />
      <BusinessProcessValidateModal v-if="activeModal === 'businessProcessValidateModal'" />
      <BusinessProcessPromoteModal v-if="activeModal === 'businessProcessPromoteModal'" />
      <DeleteModal v-if="activeModal === 'deleteModal'" :itemText="'этот БП'" />
    </div>
  </div>
</template>

<style scoped>
/* Числа в колонках версий выравниваются по разрядам, иначе 9 и 10 «прыгают». */
.bp-version {
  font-variant-numeric: tabular-nums;
}

/* Тот же кримсон, которым подкрашивается строка черновика (bota.scss). */
.bp-draft-mark {
  display: block;
  font-size: 11px;
  line-height: 1.2;
  color: #cc1043;
}

/* Описание бывает в несколько абзацев — в списке хватает двух строк. */
.bp-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
